/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import { useEffect, useState, useCallback } from 'react';
import api from './api';

/**
 * Convert URL-safe base64 (VAPID public key) into a Uint8Array required by
 * the PushManager.subscribe API.
 */
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
};

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let bin = '';
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return window.btoa(bin);
};

/**
 * usePushNotifications — hook for managing Web Push subscription.
 *
 * Returns state:
 *   supported (bool)         — Notification & PushManager available
 *   permission (string)      — 'granted' | 'denied' | 'default'
 *   subscribed (bool)        — user has an active subscription registered
 *   loading (bool)
 * Methods:
 *   subscribe(): Promise<bool>
 *   unsubscribe(): Promise<bool>
 *   sendTest(): Promise<{sent: number}>
 */
export function usePushNotifications() {
  const supported = typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;

  const [permission, setPermission] = useState(() =>
    supported && typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
      setPermission(Notification.permission);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[push] refresh subscription state failed:', err);
      }
    }
  }, [supported]);

  useEffect(() => { refresh(); }, [refresh]);

  const subscribe = useCallback(async () => {
    if (!supported) return false;
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return false;

      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get('/push/vapid-public-key');
      const applicationServerKey = urlBase64ToUint8Array(data.public_key);

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
      }
      const json = sub.toJSON();
      const keys = json.keys || {
        p256dh: arrayBufferToBase64(sub.getKey('p256dh')),
        auth: arrayBufferToBase64(sub.getKey('auth')),
      };
      await api.post('/push/subscribe', {
        endpoint: sub.endpoint,
        keys,
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      return true;
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[push] subscribe flow failed:', err);
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return false;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try {
          await api.post('/push/unsubscribe', { endpoint: sub.endpoint });
        } catch (unsubErr) {
          // Server-side unsubscribe is best-effort — we still want to remove the
          // browser-side subscription below so the user isn't spammed.
          if (process.env.NODE_ENV !== 'production') {
            console.debug('[push] server unsubscribe failed (continuing):', unsubErr);
          }
        }
        await sub.unsubscribe();
      }
      setSubscribed(false);
      return true;
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[push] unsubscribe flow failed:', err);
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const sendTest = useCallback(async () => {
    try { const r = await api.post('/push/test'); return r.data; }
    catch { return { sent: 0 }; }
  }, []);

  return { supported, permission, subscribed, loading, subscribe, unsubscribe, sendTest, refresh };
}

export default usePushNotifications;
